import { useQuery } from '@tanstack/react-query'
import {
  v2GetPortfolioOutreachStatusOptions,
  v2GetPortfolioQueueOptions,
  v2GetStartupOutreachOverviewOptions,
} from '@workspace/api-backend/react-query'
import { useStartupContext } from '../../../contexts/startup-guard'
import { useStartupMatchesQuery } from '../../../hooks/api'
import { deriveContactsToReach } from '../../../lib/matches'
import { useStartupFeatureFlagsQuery } from '../../feature-flags/hooks/use-startup-feature-flags'
import { OUTREACH_UNLOCK_STATUSES } from '../types'
import { useOutreachQueueQuery } from './use-outreach-queue'
import { useOutreachStatusQuery } from './use-outreach-status'

// Statuses that only exist once a campaign has been launched. Shared so the
// "launched vs setup" decision is made identically on every surface.
const LAUNCHED_STATUSES = new Set(['READY', 'PAUSED', 'PAUSED_DUE_TO_BUDGET', 'ERROR'])

/** Combined outreach funnel across both campaigns. */
export interface OutreachFunnelStats {
  /** Contacts still queued to reach (investor + portfolio). */
  contacts: number
  sent: number
  accepted: number
  replies: number
  /** Portfolio-only — investors don't produce warm intros. */
  warmIntros: number
  /** Investor-only metrics (portfolio has no profile-view / call data). */
  profileViews: number
  returnVisits: number
  callsBooked: number
}

/**
 * Single source of truth for the outreach funnel, summed across BOTH the
 * investor and portfolio campaigns. The founder dashboard and the outreach page
 * both read this so their numbers never drift. Each channel only contributes
 * while it's actually enabled.
 */
export function useOutreachFunnelStats(startupId: string): OutreachFunnelStats {
  const overviewQuery = useQuery(v2GetStartupOutreachOverviewOptions({ path: { id: startupId } }))
  const overview = overviewQuery.data?.status === 'ok' ? overviewQuery.data.data : null
  const statusQuery = useOutreachStatusQuery(startupId)
  const investorOn = statusQuery.data?.directInvestorOutreachEnabled ?? true
  const queueQuery = useOutreachQueueQuery(startupId)

  const flagsQuery = useStartupFeatureFlagsQuery(startupId)
  const portfolioStatusQuery = useQuery(
    v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } })
  )
  const portfolioData =
    portfolioStatusQuery.data?.status === 'ok' ? portfolioStatusQuery.data.data : null
  const portfolioOn =
    (flagsQuery.data?.portfolioOutreach ?? false) && (portfolioData?.enabled ?? false)
  const portfolioQueueQuery = useQuery(v2GetPortfolioQueueOptions({ path: { id: startupId } }))
  const portfolioQueue =
    portfolioQueueQuery.data?.status === 'ok' ? portfolioQueueQuery.data.data : null

  // Investor side — overview KPI + the live queue for "still queued".
  const investorQueued = investorOn
    ? (queueQuery.data?.filter((i) => !['finished', 'in_progress', 'engaged'].includes(i.status))
        .length ?? 0)
    : 0
  // Gate contacts on channel being active, but always show historical KPI data.
  const inv = overview ?? null

  // Portfolio side — derived from the queue buckets (one row per founder).
  // Gate contacts on channel being active, but always show historical interaction data.
  const pfForContacts = portfolioOn && portfolioQueue ? portfolioQueue : null
  const pf = portfolioQueue ?? null
  const pfAll = pf ? [...pf.queued, ...pf.active, ...pf.warmIntro, ...pf.finished] : []
  const pfCount = (pred: (m: (typeof pfAll)[number]) => boolean) => pfAll.filter(pred).length

  return {
    contacts: investorQueued + (pfForContacts ? pfForContacts.queued.length : 0),
    sent: (inv?.funnel.sent ?? 0) + pfCount((m) => m.pushedAt != null),
    accepted: (inv?.kpi.accepted ?? 0) + pfCount((m) => m.connectionAcceptedAt != null),
    replies: (inv?.kpi.replied ?? 0) + pfCount((m) => m.firstReplyAt != null),
    warmIntros: pfCount((m) => m.warmIntroConfirmedAt != null),
    profileViews: inv?.kpi.profileViews ?? 0,
    returnVisits: inv?.kpi.returnVisits ?? 0,
    callsBooked: inv?.kpi.callsBooked ?? 0,
  }
}

/**
 * Combined "Interactions so far" across both campaigns — accepts + replies +
 * warm intros + profile views + calls. Derived from {@link useOutreachFunnelStats}
 * so the dashboard and outreach hero never drift.
 */
export function useOutreachInteractions(startupId: string): number {
  const s = useOutreachFunnelStats(startupId)
  return s.accepted + s.replies + s.warmIntros + s.profileViews + s.callsBooked
}

/**
 * Investor contacts the pre-launch board shows, before any leads exist. Until the
 * campaign is built there is no queue to read, so this per-fund derivation is the
 * only count available — shared by the "contacts to reach" headline and the
 * approval counts so the two can't drift.
 */
function useInvestorPreLaunchContacts(startupId: string): { count: number; estimated: boolean } {
  const { investorMatchesUnlocked } = useStartupContext()
  const statusQuery = useOutreachStatusQuery(startupId)
  const status = statusQuery.data ?? null
  const matchesQuery = useStartupMatchesQuery(startupId, 50)
  const isUnlocked =
    investorMatchesUnlocked ||
    (status?.status != null && OUTREACH_UNLOCK_STATUSES.has(status.status))
  const visibleMatches = (matchesQuery.data?.data?.matches ?? []).filter(
    (m) => isUnlocked || m.source !== 'paid_eval'
  )
  const lockedSummary = matchesQuery.data?.data?.lockedSummary ?? null
  const totalMatches =
    lockedSummary?.totalRemaining ?? matchesQuery.data?.data?.total ?? visibleMatches.length
  return deriveContactsToReach(
    totalMatches,
    matchesQuery.data?.data?.contactReach,
    visibleMatches.reduce((sum, m) => sum + m.investorContacts.length, 0)
  )
}

/** Whether outreach leads actually exist yet (campaign built and live). */
function useLeadsMaterialized(startupId: string): boolean {
  const statusQuery = useOutreachStatusQuery(startupId)
  const status = statusQuery.data?.status
  return status != null && LAUNCHED_STATUSES.has(status)
}

/** "Contacts waiting for your approval" broken down per channel + the total. */
export interface OutreachReviewCounts {
  /** Investor contacts awaiting approval (0 when the investor channel is off). */
  investorAwaiting: number
  /** Portfolio founders awaiting approval (0 when the portfolio channel is off). */
  portfolioAwaiting: number
  /** Sum of both — the number shown in the topbar CTA and the approval banner. */
  total: number
}

/**
 * Single source of truth for "contacts waiting for your approval", summed across
 * both campaigns. The investor side only counts while its channel is on; the
 * portfolio side only while its channel is active. The topbar review CTA and the
 * outreach page's approval banner both read this so their numbers never drift.
 */
export function useOutreachReviewCounts(
  startupId: string,
  options?: { enabled?: boolean }
): OutreachReviewCounts {
  const enabled = options?.enabled ?? true

  // Count investor contacts awaiting approval whenever they're materialized —
  // they show on the board (and in the hero "contacts to reach") regardless of
  // the Choose-Campaigns channel flag, so the approval count must include them
  // too, or the topbar/banner would undercount what the founder actually sees.
  //
  // Before the campaign is built there are no leads to read a status from, yet
  // the pre-launch board already lists every contact the founder has to approve.
  // Counting only the queue there would report 0 next to a full board.
  const queueQuery = useOutreachQueueQuery(startupId, { enabled })
  const leadsMaterialized = useLeadsMaterialized(startupId)
  const preLaunchInvestorContacts = useInvestorPreLaunchContacts(startupId)
  const investorAwaiting = leadsMaterialized
    ? (queueQuery.data?.filter((i) => i.status === 'awaiting').length ?? 0)
    : preLaunchInvestorContacts.count

  const flagsQuery = useStartupFeatureFlagsQuery(startupId)
  const portfolioStatusQuery = useQuery({
    ...v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }),
    enabled,
  })
  const portfolioData =
    portfolioStatusQuery.data?.status === 'ok' ? portfolioStatusQuery.data.data : null
  const portfolioOn =
    (flagsQuery.data?.portfolioOutreach ?? false) && (portfolioData?.enabled ?? false)
  const portfolioQueueQuery = useQuery({
    ...v2GetPortfolioQueueOptions({ path: { id: startupId } }),
    enabled,
  })
  const portfolioQueue =
    portfolioQueueQuery.data?.status === 'ok' ? portfolioQueueQuery.data.data : null
  const portfolioAwaiting = portfolioOn
    ? (portfolioQueue?.queued.filter((i) => i.approvalState === 'awaiting').length ?? 0)
    : 0

  return { investorAwaiting, portfolioAwaiting, total: investorAwaiting + portfolioAwaiting }
}

/** Combined "contacts to reach" across both channels. */
export interface ContactsToReach {
  /** Investor + portfolio contacts the campaign will reach. */
  count: number
  /** True while the investor side is a per-fund estimate (research incomplete). */
  estimated: boolean
}

/**
 * Single source of truth for the "Contacts to reach" headline, summed across the
 * investor and portfolio channels. The dashboard, the outreach hero, and the
 * wallet-side stat all read this so their numbers never drift.
 *
 * - Launched: the exact live queued count (via {@link useOutreachFunnelStats}).
 * - Pre-launch: the investor per-fund estimate plus the portfolio founder count.
 *   The investor side only counts while its channel is enabled; the portfolio
 *   side counts whenever the channel flag is on (it auto-enables during setup).
 */
export function useOutreachContactsToReach(startupId: string): ContactsToReach {
  const statusQuery = useOutreachStatusQuery(startupId)
  const status = statusQuery.data ?? null
  // The live queue is only populated once the campaign actually goes live (READY+
  // — the leads are created). While the row sits at AWAITING_CAMPAIGN (still
  // assembling), no leads exist yet, so the queue is empty — keep showing the
  // estimate during that window instead of dropping to 0.
  const materialized = status?.status != null && LAUNCHED_STATUSES.has(status.status)
  const investorEnabled = status?.directInvestorOutreachEnabled ?? true

  const funnelStats = useOutreachFunnelStats(startupId)

  // Investor pre-launch estimate — mirrors the matches/dashboard derivation.
  const matchesQuery = useStartupMatchesQuery(startupId, 50)
  const { count: investorContacts, estimated } = useInvestorPreLaunchContacts(startupId)

  // Portfolio founder count — gated on the channel flag (auto-enabled in setup).
  const flagsQuery = useStartupFeatureFlagsQuery(startupId)
  const portfolioFlagOn = flagsQuery.data?.portfolioOutreach ?? false
  const portfolioStatusQuery = useQuery(
    v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } })
  )
  const portfolioData =
    portfolioStatusQuery.data?.status === 'ok' ? portfolioStatusQuery.data.data : null
  const portfolioContacts = portfolioFlagOn ? (portfolioData?.contactsCount ?? 0) : 0

  // Even for a live campaign, research may still be running — show ~ if so.
  const liveResearchPending =
    materialized &&
    (!matchesQuery.data?.data?.contactReach?.isComplete ||
      (portfolioFlagOn && (portfolioData?.researchInProgress ?? false)))

  if (materialized) return { count: funnelStats.contacts, estimated: liveResearchPending }
  return {
    count: (investorEnabled ? investorContacts : 0) + portfolioContacts,
    estimated,
  }
}
